import React, { Component } from 'react';
import moment from 'moment';

/**
 * Next Run Countdown Component
 * Real-time countdown timer showing time until next service execution
 * Updates every second to show accurate countdown
 *
 * Props:
 * - nextRun: DateTime string of next scheduled run (NULL for stopped services)
 * - accuracy: "Estimated" or "Exact" (affects styling)
 * - isRunning: 1 if running, 0 if stopped (Hybrid Approach support)
 * - isExecuting: 1 if actively executing, 0 if idle (v2.8)
 * - executionStartTime: DateTime when current execution started (v2.8)
 * - canViewDuration: Boolean - if true, shows duration alongside "Executing..." (v2.8)
 * - lastScheduledBeforeStop: Schedule preserved when service stopped (forensic data)
 *
 * Display Logic (Hybrid Approach):
 * - If service executing (isExecuting=1): "Executing... (Xm Xs)" in blue with pulse
 *   Duration only shown if canViewDuration=true (permission-controlled)
 * - If service stopped (isRunning=0): "Stopped" in gray (clean UI)
 * - If overdue (running): "Overdue" in red
 * - If > 1 day: Shows date (e.g., "Oct 22, 14:30")
 * - If < 1 day: Shows relative time (e.g., "in 5 minutes")
 * - If null: "N/A"
 */
class NextRunCountdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      timeUntil: '',
      isOverdue: false,
      isStopped: false,
      isExecutingState: false,
      executionDuration: '',
    };
    this.updateInterval = null;
  }

  componentDidMount() {
    this.updateCountdown();
    // Update every second for real-time countdown
    this.updateInterval = setInterval(this.updateCountdown, 1000);
  }

  componentWillUnmount() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  componentDidUpdate(prevProps) {
    // Update immediately if nextRun, isRunning, or isExecuting prop changes
    if (prevProps.nextRun !== this.props.nextRun ||
        prevProps.isRunning !== this.props.isRunning ||
        prevProps.isExecuting !== this.props.isExecuting) {
      this.updateCountdown();
    }
  }

  // Helper to format execution duration
  formatExecutionDuration = (executionStartTime) => {
    if (!executionStartTime) return '';

    const startTime = moment.utc(executionStartTime);
    const now = moment();
    const duration = moment.duration(now.diff(startTime));

    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes() % 60);
    const seconds = Math.floor(duration.asSeconds() % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  updateCountdown = () => {
    const { nextRun, isRunning, isExecuting, executionStartTime } = this.props;

    // NEW (v2.8): Check if service is currently executing
    // Executing services show "Executing..." badge with pulse animation
    if (isExecuting === 1) {
      const duration = this.formatExecutionDuration(executionStartTime);
      this.setState({
        timeUntil: 'Executing...',
        isOverdue: false,
        isStopped: false,
        isExecutingState: true,
        executionDuration: duration
      });
      return;
    }

    // HYBRID APPROACH: Check if service is stopped
    // Stopped services show "Stopped" badge, not countdown/overdue
    if (isRunning === 0) {
      this.setState({ timeUntil: 'Stopped', isOverdue: false, isStopped: true, isExecutingState: false });
      return;
    }

    // Handle null or undefined nextRun (for running services)
    if (!nextRun) {
      this.setState({ timeUntil: 'N/A', isOverdue: false, isStopped: false, isExecutingState: false });
      return;
    }

    const now = moment();
    // Parse nextRun as UTC and convert to local timezone for comparison
    const next = moment.utc(nextRun).local();
    const duration = moment.duration(next.diff(now));

    // Check if overdue (past the scheduled time)
    if (duration.asSeconds() < 0) {
      this.setState({ timeUntil: 'Overdue', isOverdue: true, isStopped: false, isExecutingState: false });
    } else if (duration.asDays() >= 1) {
      // Show formatted date if more than 1 day away
      this.setState({
        timeUntil: next.format('MMM DD, HH:mm'),
        isOverdue: false,
        isStopped: false,
        isExecutingState: false
      });
    } else if (duration.asHours() >= 1) {
      // Show hours and minutes if less than 1 day but more than 1 hour
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes() % 60);
      this.setState({
        timeUntil: `in ${hours}h ${minutes}m`,
        isOverdue: false,
        isStopped: false,
        isExecutingState: false
      });
    } else if (duration.asMinutes() >= 1) {
      // Show minutes if less than 1 hour
      const minutes = Math.floor(duration.asMinutes());
      this.setState({
        timeUntil: `in ${minutes} min`,
        isOverdue: false,
        isStopped: false,
        isExecutingState: false
      });
    } else {
      // Show seconds if less than 1 minute
      const seconds = Math.floor(duration.asSeconds());
      this.setState({
        timeUntil: `in ${seconds} sec`,
        isOverdue: false,
        isStopped: false,
        isExecutingState: false
      });
    }
  };

  render() {
    const { timeUntil, isOverdue, isStopped, isExecutingState, executionDuration } = this.state;
    const { lastScheduledBeforeStop, canViewDuration } = this.props;

    // Apply styling based on status
    let className = '';
    if (isExecutingState) {
      className = 'text-primary executing-pulse';  // Blue with pulse animation for executing
    } else if (isOverdue) {
      className = 'text-danger font-weight-bold';  // Red for overdue
    } else if (isStopped) {
      className = 'text-muted';  // Gray for stopped
    }

    // Build tooltip for stopped services (show historical schedule)
    const title = isStopped && lastScheduledBeforeStop
      ? `Stopped - Was scheduled for ${moment.utc(lastScheduledBeforeStop).local().format('MMM DD, YYYY HH:mm')}`
      : '';

    // Enhanced display: Show "Executing... (duration)" when service is executing
    // Duration only shown if user has permission (canViewDuration)
    if (isExecutingState) {
      return (
        <span className={className}>
          {timeUntil}
          {canViewDuration && executionDuration && (
            <span className="ml-1">({executionDuration})</span>
          )}
        </span>
      );
    }

    // Enhanced display: Show historical schedule as subtitle for stopped services
    if (isStopped && lastScheduledBeforeStop) {
      // Parse lastScheduledBeforeStop as UTC and convert to local timezone
      const lastScheduled = moment.utc(lastScheduledBeforeStop).local();
      const now = moment();
      const daysAgo = now.diff(lastScheduled, 'days');
      const hoursAgo = now.diff(lastScheduled, 'hours');
      
      let timeAgoText = '';
      if (daysAgo > 0) {
        timeAgoText = `${daysAgo} day${daysAgo > 1 ? 's' : ''} ago`;
      } else if (hoursAgo > 0) {
        timeAgoText = `${hoursAgo} hour${hoursAgo > 1 ? 's' : ''} ago`;
      } else {
        timeAgoText = 'Recently';
      }

      return (
        <div className="d-flex flex-column" title={title}>
          <span className={className}>{timeUntil}</span>
          <small className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
            Was scheduled {timeAgoText}
          </small>
        </div>
      );
    }

    return (
      <span className={className} title={title}>
        {timeUntil}
      </span>
    );
  }
}

export default NextRunCountdown;
